import { useState, useEffect, useRef } from 'react';
import { GoogleMap, Marker, Circle } from '@react-google-maps/api';

const defaultCenter = { lat: 52.5, lng: -1.5 };

const getMarkerIcon = (eventType, isSelected) => {
  const colors = { meets: '#e63946', auctions: '#f4a261', races: '#2a9d8f', autojumbles: '#457b9d', default: '#888888' };
  const emojis = { meets: '🏎', auctions: '💸', races: '🏁', autojumbles: '🔧', default: '📍' };
  const color = isSelected ? '#ffffff' : (colors[eventType] || colors.default);
  const emoji = emojis[eventType] || emojis.default;
  const size = isSelected ? 50 : 40;
  const svg = '<svg xmlns="http://www.w3.org/2000/svg" width="' + size + '" height="' + (size + 10) + '" viewBox="0 0 40 50"><path d="M20 0C9 0 0 9 0 20c0 15 20 30 20 30S40 35 40 20C40 9 31 0 20 0z" fill="' + color + '" stroke="#e63946" stroke-width="2"/><circle cx="20" cy="20" r="13" fill="white"/><text x="20" y="26" text-anchor="middle" font-size="16" fill="black">' + emoji + '</text></svg>';
  return 'data:image/svg+xml,' + encodeURIComponent(svg);
};

const MILES_TO_METRES = 1609.34;

function MapView({ events, selectedEvent, setSelectedEvent, mapExpanded, setMapExpanded, mapFullScreen, setMapFullScreen, getCalendarUrl, mapsLoaded, filterLocation, distanceEnabled, distanceMiles }) {
  const [userLocation, setUserLocation] = useState(defaultCenter);
  const [windowHeight, setWindowHeight] = useState(window.innerHeight);
  const mapRef = useRef(null);

  useEffect(() => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        position => setUserLocation({ lat: position.coords.latitude, lng: position.coords.longitude }),
        () => console.log('Location access denied')
      );
    }

    let resizeTimer;
    const handleResize = () => {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(() => {
        setWindowHeight(window.innerHeight);
        if (mapRef.current) {
          mapRef.current.panTo(mapRef.current.getCenter());
        }
      }, 300);
    };

    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
      clearTimeout(resizeTimer);
    };
  }, []);

  const mapHeightPx = mapFullScreen ? windowHeight : Math.floor(windowHeight * 0.55);
  const circleCenter = filterLocation ? { lat: filterLocation.lat, lng: filterLocation.lng } : userLocation;
  const mapCenter = selectedEvent
    ? { lat: selectedEvent.latitude, lng: selectedEvent.longitude }
    : circleCenter;

  return (
    <div>
      <button
        className={mapExpanded ? 'map-toggle-btn open' : 'map-toggle-btn'}
        onClick={() => {
          setMapExpanded(!mapExpanded);
          if (mapFullScreen) setMapFullScreen(false);
          if (mapExpanded) setSelectedEvent(null);
        }}
      >
        {mapExpanded ? '▲ HIDE MAP' : '▼ SEE MAP'}
      </button>

      {mapExpanded && (
        <div style={{ width: '100%', height: mapHeightPx + 'px', position: 'relative' }}>
          <button className="fullscreen-btn" onClick={() => setMapFullScreen(!mapFullScreen)}>
            {mapFullScreen ? '⤡ Exit' : '⤢ Full Screen'}
          </button>

          <GoogleMap
            mapContainerStyle={{ width: '100%', height: '100%' }}
            center={mapCenter}
            zoom={selectedEvent ? 13 : 7}
            mapTypeId="satellite"
            onClick={() => setSelectedEvent(null)}
            onLoad={map => { mapRef.current = map; }}
            options={{
              zoomControl: true,
              mapTypeControl: false,
              streetViewControl: false,
              fullscreenControl: false,
              zoomControlOptions: { position: 9 },
              gestureHandling: 'greedy',
            }}
          >
            {mapsLoaded && events.map(event => (
              <Marker
                key={event.id}
                position={{ lat: event.latitude, lng: event.longitude }}
                title={event.title}
                icon={{
                  url: getMarkerIcon(event.event_type, selectedEvent?.id === event.id),
                  scaledSize: selectedEvent?.id === event.id
                    ? new window.google.maps.Size(50, 60)
                    : new window.google.maps.Size(40, 50),
                  anchor: selectedEvent?.id === event.id
                    ? new window.google.maps.Point(25, 60)
                    : new window.google.maps.Point(20, 50)
                }}
                onClick={() => setSelectedEvent(event)}
              />
            ))}
            {mapsLoaded && distanceEnabled && circleCenter && (
              <Circle
                center={circleCenter}
                radius={distanceMiles * MILES_TO_METRES}
                options={{
                  strokeColor: '#e63946',
                  strokeOpacity: 0.9,
                  strokeWeight: 2,
                  fillColor: '#e63946',
                  fillOpacity: 0.08,
                }}
              />
            )}
          </GoogleMap>

          {selectedEvent && (
            <div className="map-bottom-sheet" onClick={e => e.stopPropagation()}>
              <div className="map-bottom-sheet-handle" onClick={() => setSelectedEvent(null)} />
              <div className="map-bottom-sheet-content">
                <button className="close-btn" onClick={() => setSelectedEvent(null)}>✕</button>
                <div className="sheet-event-type-row">
                  <span className="event-type">{selectedEvent.event_type}</span>
                  {selectedEvent.marque && <span className="event-type">{selectedEvent.marque}</span>}
                </div>
                <h2 className="sheet-title">{selectedEvent.title}</h2>
                <div className="sheet-meta">
                  <span>📅 {selectedEvent.date}</span>
                  {selectedEvent.start_time && <span>🕐 {selectedEvent.start_time}</span>}
                  {selectedEvent.location_name && <span>📍 {selectedEvent.location_name}</span>}
                </div>
                {selectedEvent.description && <p className="sidebar-description">{selectedEvent.description}</p>}
                <div className="sheet-actions">
                  <a href={'https://www.google.com/maps/dir/?api=1&destination=' + selectedEvent.latitude + ',' + selectedEvent.longitude} target="_blank" rel="noreferrer" className="sheet-action-btn directions-btn">🗺 Directions</a>
                  <a href={getCalendarUrl(selectedEvent)} target="_blank" rel="noreferrer" className="sheet-action-btn calendar-btn">📅 Calendar</a>
                  {selectedEvent.external_link && <a href={selectedEvent.external_link} target="_blank" rel="noreferrer" className="sheet-action-btn tickets-btn">🎟 Tickets</a>}
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default MapView;